/**
 * ComboboxFK — Searchable dropdown for FK fields with quick-add
 *
 * Supports FKOption.meta (extra info line) and FKOption.searchTags (full-text search).
 *
 * Usage:
 *   const { options, isLoading } = useCustomerLookup();
 *   <ComboboxFK
 *     value={field.state.value}
 *     onChange={field.handleChange}
 *     options={options}
 *     label="Khách hàng"
 *     quickAddLabel="Thêm KH mới"
 *     onQuickAdd={async (name) => { ... return newId; }}
 *   />
 */

import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Check, ChevronsUpDown, Plus, Search, Loader2, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn, normalizeVi, fuzzyMatchVi } from '@/lib/utils';
import type { FKOption } from '@/hooks/useFKLookup';
import { toast } from 'sonner';

interface ComboboxConfigProps {
    value: string;
    onChange: (id: string) => void;
    options: FKOption[];
    label?: string;
    placeholder?: string;
    required?: boolean;
    loading?: boolean;
    error?: boolean;
    disabled?: boolean;
    className?: string;
    /** Quick-add button label */
    quickAddLabel?: string;
    /** Quick-add handler: receives typed text, returns new item id (for simple lookups) */
    onQuickAdd?: (name: string) => Promise<string | null>;
    /** Custom click handler for quick-add (e.g. to open a modal instead of a new tab) */
    onQuickAddClick?: (name: string) => void;
    /** Also set a name field when selecting */
    onNameChange?: (name: string) => void;
    /** Hide the external label (useful when rendered inside a table cell) */
    hideLabel?: boolean;
    /** Whether the user has permission to add new items. Defaults to true. */
    canAdd?: boolean;
    /** External search change handler for backend filtering */
    onSearchChange?: (search: string) => void;
}

export function ComboboxConfig({
    value,
    onChange,
    options,
    label,
    placeholder = 'Chọn...',
    required,
    loading,
    error,
    disabled,
    quickAddLabel,
    onQuickAdd,
    onQuickAddClick,
    onNameChange,
    hideLabel,
    canAdd = true,
    onSearchChange,
}: ComboboxConfigProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [adding, setAdding] = useState(false);
    const [showQuickInput, setShowQuickInput] = useState(false);
    const [quickInputValue, setQuickInputValue] = useState('');
    const [displayLimit, setDisplayLimit] = useState(50);
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [optimisticOption, setOptimisticOption] = useState<FKOption | null>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const quickInputRef = useRef<HTMLInputElement>(null);
    const preventCloseRef = useRef(false);

    useEffect(() => {
        if (open) {
            setTimeout(() => searchInputRef.current?.focus(), 50);
            setDisplayLimit(50);
        } else {
            setSearch('');
            setShowQuickInput(false);
            setQuickInputValue('');
        }
    }, [open]);

    // Debounce search to parent
    useEffect(() => {
        if (!onSearchChange) return;
        const timer = setTimeout(() => {
            onSearchChange(search);
        }, 300);
        return () => clearTimeout(timer);
    }, [search, onSearchChange]);

    useEffect(() => {
        if (showQuickInput) {
            setTimeout(() => quickInputRef.current?.focus(), 50);
        }
    }, [showQuickInput]);

    // Filter options by search
    const filtered = useMemo(() => {
        if (!search.trim()) return options;
        return options.filter(o => {
            if (fuzzyMatchVi(o.searchTags, search)) return true;
            if (fuzzyMatchVi(o.label, search)) return true;
            if (fuzzyMatchVi(o.subLabel, search)) return true;
            if (fuzzyMatchVi(o.meta, search)) return true;
            return false;
        });
    }, [options, search]);

    // Reset activeIndex to 0 when filtered options change
    useEffect(() => {
        setActiveIndex(0);
    }, [filtered]);

    // Auto-scroll to active index
    useEffect(() => {
        if (open && listRef.current && activeIndex >= 0) {
            const activeEl = listRef.current.children[activeIndex] as HTMLElement;
            if (activeEl && activeEl.scrollIntoView) {
                activeEl.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [activeIndex, open]);

    // Find selected option with optimistic Option & value fallback for <0.1s closed-loop return
    const selected = useMemo(() => {
        const found = options?.find(
            o => o.id === value || 
                 o.label === value || 
                 (value && o.id.toLowerCase() === value.toLowerCase()) || 
                 (value && o.label.toLowerCase() === value.toLowerCase())
        );
        if (found) return found;
        if (
            optimisticOption &&
            value &&
            (optimisticOption.id === value ||
             optimisticOption.label === value ||
             optimisticOption.id.toLowerCase() === value.toLowerCase() ||
             optimisticOption.label.toLowerCase() === value.toLowerCase())
        ) {
            return optimisticOption;
        }
        if (value && typeof value === 'string' && value.trim()) {
            return { id: value, label: value };
        }
        return undefined;
    }, [options, value, optimisticOption]);

    const handleSelect = (option: FKOption) => {
        setOptimisticOption(option);
        onChange(option.id);
        if (onNameChange) onNameChange(option.label);
        setOpen(false);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        setOptimisticOption(null);
        onChange('');
        if (onNameChange) onNameChange('');
    };

    const handleQuickAdd = async (name?: string) => {
        const text = name || search.trim() || quickInputValue.trim();
        if (!onQuickAdd || !text) return;

        // Chặn tạo trùng lặp
        const normalizedText = normalizeVi(text).trim().toLowerCase();
        const isDuplicate = options.some(o => normalizeVi(o.label).trim().toLowerCase() === normalizedText);
        if (isDuplicate) {
            toast.error(`Giá trị "${text}" đã tồn tại!`);
            return;
        }

        setAdding(true);
        try {
            const newId = await onQuickAdd(text);
            if (newId) {
                setOptimisticOption({ id: newId, label: text });
                onChange(newId);
                if (onNameChange) onNameChange(text);
                setOpen(false);
            }
        } finally {
            setAdding(false);
            setShowQuickInput(false);
            setQuickInputValue('');
            preventCloseRef.current = false;
        }
    };

    const handleShowMore = useCallback(() => {
        setDisplayLimit(prev => prev + 50);
    }, []);

    return (
        <div className={cn("space-y-1.5", hideLabel && "space-y-0")}>
            {!hideLabel && label && (
                <Label className="text-xs font-medium text-slate-700">
                    {label}
                    {required && <span className="text-destructive ml-0.5">*</span>}
                </Label>
            )}
            <Popover open={open} onOpenChange={(v) => {
                if (!v && (preventCloseRef.current || showQuickInput)) return;
                setOpen(v);
            }}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        aria-label={label || placeholder}
                        disabled={disabled}
                        className={cn(
                            'w-full min-w-0 max-w-full justify-between h-9 text-sm font-normal', // không giãn theo chữ gợi ý dài (tràn hộp thoại trên điện thoại)
                            !value && 'text-muted-foreground',
                            error && 'border-destructive',
                        )}
                    >
                        <span className="truncate flex-1 min-w-0 text-left">
                            {loading && !selected ? (
                                <span className="flex items-center gap-1.5 text-slate-400">
                                    <Loader2 className="h-3 w-3 animate-spin" /> Đang tải...
                                </span>
                            ) : selected ? (
                                <span className="flex items-center gap-1.5">
                                    {selected.thumbnail && (
                                        <img src={selected.thumbnail} alt="" className="w-5 h-5 rounded object-cover shrink-0 border border-slate-200" />
                                    )}
                                    <span className="truncate">{selected.label}</span>
                                </span>
                            ) : (
                                placeholder
                            )}
                        </span>
                        <div className="flex items-center gap-0.5 ml-1 shrink-0">
                            {value && !disabled && (
                                <X
                                    className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 cursor-pointer"
                                    onClick={handleClear}
                                />
                            )}
                            <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400" />
                        </div>
                    </Button>
                </PopoverTrigger>

                <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0"
                    align="start"
                    onPointerDownOutside={(e) => { if (preventCloseRef.current || showQuickInput) e.preventDefault(); }}
                    onFocusOutside={(e) => { if (preventCloseRef.current || showQuickInput) e.preventDefault(); }}
                    onInteractOutside={(e) => { if (preventCloseRef.current || showQuickInput) e.preventDefault(); }}
                >
                    {/* Search */}
                    <div className="flex items-center gap-1.5 px-2 py-1.5 border-b">
                        <Search className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                        <Input
                            ref={searchInputRef}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder={`Tìm ${label ? label.toLowerCase() : '...'}...`}
                            className="h-7 text-xs border-0 shadow-none focus-visible:ring-0 px-0"
                            onKeyDown={e => {
                                if (e.key === 'ArrowDown') {
                                    e.preventDefault();
                                    setActiveIndex(prev => Math.min(prev + 1, filtered.length - 1));
                                } else if (e.key === 'ArrowUp') {
                                    e.preventDefault();
                                    setActiveIndex(prev => Math.max(prev - 1, 0));
                                } else if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (filtered.length > 0 && activeIndex >= 0 && activeIndex < filtered.length) {
                                        handleSelect(filtered[activeIndex]);
                                    }
                                }
                            }}
                        />
                    </div>

                    {/* Options list */}
                    <div className="max-h-[280px] overflow-y-auto" ref={listRef}>
                        {filtered.length === 0 ? (
                            <div className="px-3 py-4 text-center text-xs text-slate-400">
                                Không tìm thấy kết quả
                            </div>
                        ) : (
                            <>
                                {filtered.slice(0, displayLimit).map((option, index) => (
                                    <button
                                        key={option.id}
                                        type="button"
                                        role="option"
                                        aria-selected={option.id === value}
                                        onMouseEnter={() => setActiveIndex(index)}
                                        className={cn(
                                            'w-full flex items-start gap-2 px-2 py-2 text-left text-xs transition-colors',
                                            index === activeIndex ? 'bg-slate-100' : 'hover:bg-slate-50',
                                            option.id === value && 'apv-accent-bg-soft'
                                        )}
                                        onPointerDown={(e) => { e.preventDefault(); }}
                                        onClick={() => handleSelect(option)}
                                    >
                                        <Check
                                            className={cn(
                                                'h-3.5 w-3.5 shrink-0 mt-0.5',
                                                option.id === value ? 'apv-accent-text' : 'invisible',
                                            )}
                                        />
                                        <div className="flex-1 min-w-0 flex items-start gap-2">
                                            {option.thumbnail && (
                                                <img src={option.thumbnail} alt="" className="w-6 h-6 rounded object-cover shrink-0 border border-slate-200 mt-px" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start gap-1.5 flex-wrap">
                                                    {option.subLabel && (
                                                        <span className="font-mono text-[10px] text-indigo-600 bg-indigo-50 px-1 rounded shrink-0 mt-px">
                                                            {option.subLabel}
                                                        </span>
                                                    )}
                                                    <span className="text-slate-900 font-medium leading-tight break-words">{option.label}</span>
                                                </div>
                                                {option.meta && (
                                                    <div className="text-[10px] text-slate-500 mt-0.5 leading-tight break-words">
                                                        {option.meta}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                                {filtered.length > displayLimit && (
                                    <button
                                        type="button"
                                        onClick={handleShowMore}
                                        className="w-full px-3 py-2 text-center text-[11px] text-blue-600 hover:bg-blue-50 border-t border-dashed transition-colors flex items-center justify-center gap-1"
                                    >
                                        <ChevronDown className="h-3 w-3" />
                                        Xem thêm ({filtered.length - displayLimit} còn lại)
                                    </button>
                                )}
                            </>
                        )}
                    </div>

                    {/* Quick-add */}
                    {canAdd && quickAddLabel && (onQuickAdd  || onQuickAddClick) && (
                        <div className="border-t px-2 py-1.5">
                            {showQuickInput && onQuickAdd ? (
                                <div className="flex items-center gap-1.5">
                                    <Input
                                        ref={quickInputRef}
                                        value={quickInputValue}
                                        onChange={e => setQuickInputValue(e.target.value)}
                                        placeholder={`Nhập tên ${label ? label.toLowerCase() : '...'}...`}
                                        className="h-7 text-xs flex-1"
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' && quickInputValue.trim()) {
                                                e.preventDefault();
                                                handleQuickAdd(quickInputValue.trim());
                                            }
                                            if (e.key === 'Escape') {
                                                setShowQuickInput(false);
                                                setQuickInputValue('');
                                            }
                                        }}
                                    />
                                    <Button
                                        type="button"
                                        size="sm"
                                        disabled={adding || !quickInputValue.trim()}
                                        className="h-7 text-xs px-3 bg-emerald-600 hover:bg-emerald-700"
                                        onClick={() => handleQuickAdd(quickInputValue.trim())}
                                    >
                                        {adding ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Tạo'}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 w-7 px-0 text-slate-400 hover:text-slate-600"
                                        onClick={() => { setShowQuickInput(false); setQuickInputValue(''); preventCloseRef.current = false; }}
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    disabled={adding}
                                    className={cn(
                                        'w-full flex items-center gap-1.5 px-2 py-1.5 text-xs rounded transition-colors',
                                        'text-emerald-700 hover:bg-emerald-50 cursor-pointer',
                                    )}
                                    onMouseDown={(e) => {
                                        e.stopPropagation();
                                        if (!onQuickAddClick && !search.trim()) {
                                            preventCloseRef.current = true;
                                        }
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (onQuickAddClick) {
                                            onQuickAddClick(search.trim());
                                            setOpen(false);
                                        } else if (search.trim()) {
                                            handleQuickAdd();
                                        } else {
                                            setShowQuickInput(true);
                                        }
                                    }}
                                >
                                    {adding ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                        <Plus className="h-3.5 w-3.5" />
                                    )}
                                    <span>
                                        {adding ? 'Đang tạo...' : (search.trim() ? `${quickAddLabel}: "${search}"` : quickAddLabel)}
                                    </span>
                                </button>
                            )}
                        </div>
                    )}
                </PopoverContent>
            </Popover>
        </div>
    );
}
